import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Heading } from '@uk-source-web/heading';
import { Icon } from '@uk-source-web/icon';

import { TenantPortingOrderConfirmationProps } from './TenantPortingOrderConfirnmation.types';
import {
  TenantPortingContainer,
  TenantPortingHeader,
  TenantPortingItemsWrapper,
} from './TenantPortingOrderConfirmation.styles';
import TenantPortingItem from './TenantPortingProducts/TenantPortingItem';
import { deleteBasket, useAppDispatch, useAppSelector } from '@/store';
import CompletedPurchaseStyles, {
  CompletedPurchaseStepper,
} from '../OrderCompletedPurchase/OrderCompletedPurchase.styles';
import { BasketHorizontalStepper } from '../SWBasket/BasketHorizontalStepper/BasketHorizontalStepper';
import { EmotionJSX } from '@emotion/react/dist/declarations/src/jsx-namespace';
import { TenantPortingItemProps } from './TenantPortingProducts/TenantPortingItem.types';
import { useLocation } from '@reach/router';

const TENANT_PORTING_BASKET_SNAPSHOT_KEY = 'tenantPortingBasketSnapshot';

const TenantPortingOrderConfirmation = ({
  title,
  heading,
  calloutMessages,
  calloutMessageNonMsft,
}: TenantPortingOrderConfirmationProps) => {
    const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const dispatch = useAppDispatch();
  const { userBasket } = useAppSelector(({ userBasket: { data } }) => ({
    userBasket: data,
  }));
 
  const [basketSnapshot, setBasketSnapshot] = useState<any>(null);
  const hasSnapshotted = useRef(false);
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    const cachedBasketSnapshot = sessionStorage.getItem(TENANT_PORTING_BASKET_SNAPSHOT_KEY);
    if (!cachedBasketSnapshot) return;

    try {
      setBasketSnapshot(JSON.parse(cachedBasketSnapshot));
      hasSnapshotted.current = true;
    } catch {
      sessionStorage.removeItem(TENANT_PORTING_BASKET_SNAPSHOT_KEY);
    }
  }, []);

  useEffect(() => {
    if (!hasSnapshotted.current && userBasket?.items?.length > 0) {
      setBasketSnapshot(userBasket);
      sessionStorage.setItem(TENANT_PORTING_BASKET_SNAPSHOT_KEY, JSON.stringify(userBasket));
      hasSnapshotted.current = true;
      dispatch(deleteBasket());
    }
  }, [userBasket, dispatch]);


useEffect(() => {
  if (previousPathname.current !== location.pathname) {
    sessionStorage.removeItem(TENANT_PORTING_BASKET_SNAPSHOT_KEY);
  }

  previousPathname.current = location.pathname;
}, [location.pathname]);

  const items = basketSnapshot?.items ?? [];
 
  return (
    <>
      <CompletedPurchaseStepper>
        <BasketHorizontalStepper currentStep={3} />
      </CompletedPurchaseStepper>
      <CompletedPurchaseStyles>
        <TenantPortingContainer>
          <TenantPortingHeader>
            <Heading size={3} weight={4}>
              {title}
            </Heading>
            <Icon name={'tick-outline'} group="hifiLight" />
          </TenantPortingHeader>
          <hr style={{ border: 'none', borderTop: '1px solid #7E7E7E', margin: '10px 0' }} />
          {/* Render Microsoft items first */}
          {items
            .filter((product: { msftProduct: any }) => product.msftProduct)
            .map((product: EmotionJSX.IntrinsicAttributes & TenantPortingItemProps) => {
              const initiallyOpenIds = [product.id];
              return (
                <TenantPortingItemsWrapper key={product.id}>
                  <TenantPortingItem
                    key={product.id}
                    {...product}
                    queryParams={queryParams}
                    calloutMessages={calloutMessages}
                    calloutMessageNonMsft={calloutMessageNonMsft}
                    initiallyOpenIds={initiallyOpenIds}
                  />
                </TenantPortingItemsWrapper>
              );
            })}

          {/* Single heading before non-Microsoft items */}
          {items.some((product: { msftProduct: any }) => !product.msftProduct) && (
            <Heading size={2} weight={4}>
              {heading}
            </Heading>
          )}

          {/* Render non-Microsoft items after the heading */}
          {items
            .filter((product: { msftProduct: any }) => !product.msftProduct)
            .map((product: EmotionJSX.IntrinsicAttributes & TenantPortingItemProps) => {
              const initiallyOpenIds = [product.id];
              return (
                <TenantPortingItemsWrapper key={product.id}>
                  <TenantPortingItem
                    key={product.id}
                    {...product}
                    queryParams={queryParams}
                    calloutMessages={calloutMessages}
                    calloutMessageNonMsft={calloutMessageNonMsft}
                    initiallyOpenIds={initiallyOpenIds}
                  />
                </TenantPortingItemsWrapper>
              );
            })}
        </TenantPortingContainer>
      </CompletedPurchaseStyles>
    </>
  );
};

export default TenantPortingOrderConfirmation;
